const STORAGE_KEY = 'selectedOrganizationId';
const CROP_SEASONS_KEY = 'selectedCropSeasons';
const RELOAD_ATTEMPT_KEY = 'organizationReloadAttempt';

interface MirroredSelection {
  userId?: string;
  organizationId: string;
}

// Both stores can throw rather than return null - Safari private mode rejects
// writes once over quota, and some privacy modes make sessionStorage itself
// unreachable - so every access is guarded. A lost selection degrades to the
// default organization; a thrown error would break the app bootstrap.
const guard = <T>(read: () => T, fallback: T): T => {
  try {
    return read();
  } catch {
    return fallback;
  }
};

const readLocal = (): string | null =>
  guard(() => localStorage.getItem(STORAGE_KEY), null);

const writeLocal = (organizationId: string): void =>
  guard(() => localStorage.setItem(STORAGE_KEY, organizationId), undefined);

// localStorage stringifies on write, JSON does not - so the mirror is the one
// path that can hand back a non-string id and break strict comparisons against
// `organization.id`.
const readMirror = (): MirroredSelection | null =>
  guard(() => {
    const raw = globalThis.sessionStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as MirroredSelection;
    return { ...parsed, organizationId: String(parsed.organizationId) };
  }, null);

/**
 * Drops the selection from both stores, along with the crop seasons scoped to
 * it - season ids belong to one organization, so keeping them would filter the
 * next organization by seasons it does not have.
 */
export const clearSelectedOrganization = (): void => {
  guard(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CROP_SEASONS_KEY);
  }, undefined);
  guard(() => globalThis.sessionStorage?.removeItem(STORAGE_KEY), undefined);
};

/**
 * Reads the organization the user is currently working in.
 *
 * The selection is mirrored into sessionStorage because localStorage is wiped
 * wholesale on a session reset (`clearAccessToken`). Without the mirror the
 * bootstrap sees no selection and silently drops the user into the first
 * organization of their list, mid-task.
 *
 * @param userId - id of the signed-in user, when known. A mirror left behind by
 * a different user on this tab is discarded rather than restored.
 * @returns the selected organization id, or null when nothing is selected.
 */
export const getSelectedOrganizationId = (userId?: string): string | null => {
  const mirrored = readMirror();
  if (mirrored?.userId && userId && mirrored.userId !== userId) {
    clearSelectedOrganization();
    return null;
  }
  const stored = readLocal();
  if (stored) {
    return stored;
  }
  if (!mirrored) {
    return null;
  }
  writeLocal(mirrored.organizationId);
  return mirrored.organizationId;
};

/**
 * Persists the organization the user is working in to both stores.
 *
 * @param organizationId - id of the organization to select.
 * @param userId - id of the signed-in user, when known, so the mirror is not
 * honoured for anyone else signing in on this tab.
 */
export const setSelectedOrganizationId = (
  organizationId: string,
  userId?: string
): void => {
  writeLocal(organizationId);
  guard(
    () =>
      globalThis.sessionStorage?.setItem(
        STORAGE_KEY,
        JSON.stringify({ userId, organizationId })
      ),
    undefined
  );
};

/**
 * Counts consecutive 401-triggered reloads, which are only ever one page load
 * apart because a successful bootstrap clears the count.
 *
 * @returns true while the selection is still worth keeping (the first attempt,
 * i.e. an expired token), false once a reload has already failed to help - at
 * which point the selected organization is itself the likely cause.
 */
export const registerOrganizationReloadAttempt = (): boolean => {
  const attempted = guard(
    () => globalThis.sessionStorage?.getItem(RELOAD_ATTEMPT_KEY),
    null
  );
  if (attempted) {
    guard(
      () => globalThis.sessionStorage?.removeItem(RELOAD_ATTEMPT_KEY),
      undefined
    );
    return false;
  }
  guard(
    () => globalThis.sessionStorage?.setItem(RELOAD_ATTEMPT_KEY, '1'),
    undefined
  );
  return true;
};

/** Resets the 401 reload counter once an organization has loaded. */
export const clearOrganizationReloadAttempts = (): void =>
  guard(
    () => globalThis.sessionStorage?.removeItem(RELOAD_ATTEMPT_KEY),
    undefined
  );
